import { TableColumnsType } from "antd";
import { ColumnType } from "antd/es/table";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  CustomError,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import TableButton from "components/atoms/buttons/TableButton";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import ToggleComponent from "components/atoms/toggle/Toggle";
import { useMenu } from "hook/useMenu";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { formatNumber } from "utils/functions";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { getAccountTimeFormatDate, getTodayToUTCDate } from "utils/timeUtil";
import { isNull } from "lodash";
import FeaturesPopupPresenter from "./FeaturesPopupPresenter";
import { useQuery } from "react-query";
import {
  FeaturesPopupData,
  FeaturesPopupListData,
  GetFeaturesPopupListResponse,
} from "api/interfaces/settingsInterface.interface";
import { getFeaturesPopupList } from "api/settingAPI";
import useApiError from "hook/useApiError";
import { useIsMobile } from "components/MediaQuery";
import { StatusType } from "components/blocks/status/Status";

const initialQueryinfo: BasePagination = {
  ...baseQueryInfo,
  sortType: "createdAt",
};

export const initialData: FeaturesPopupData = {
  title: "",
  startDate: getTodayToUTCDate("America/Detroit"),
  endDate: getTodayToUTCDate("America/Detroit", true),
  isActive: false,
  slides: [
    {
      title: "",
      url: "",
      description: "",
      slideOrder: 1,
      id: new Date().getTime().toString(),
    },
  ],
};

export default function FeaturesPopupContainer() {
  const location = useLocation();
  const { queryNavigate } = useMenu({});
  const isMobile = useIsMobile();

  const [status, setStatus] = useState<StatusType>({
    totalTitle: "Total",
    totalValue: 0,
    items: [],
    isMobileShow: {
      totalTitle: true,
      totalValue: true,
      items: true,
    },
    cloudStatus: false,
  });
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [data, setData] = useState<FeaturesPopupListData[]>([]);
  const [queryInfo, setQueryInfo] = useState<BasePagination>({
    ...initialQueryinfo,
  });
  const queryKey = useQueryKey(queryInfo);

  const [isAddFeaturesModal, setIsAddFeaturesModal] = useState<boolean>(false);
  const [isViewFeaturesModal, setIsViewFeaturesModal] =
    useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<FeaturesPopupData>({
    ...initialData,
  });
  const { handleErrorNotify, intlString, lang, createCustomError } =
    useApiError();

  const { refetch, isLoading } = useQuery(
    ["featuresPopupList", queryKey],
    () =>
      getFeaturesPopupList({
        payload: {
          ...queryInfo,
        },
      }),
    {
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: GetFeaturesPopupListResponse) => {
        if (res.error !== 0 || !res.result) {
          const error = createCustomError(
            res,
            intlString(lang.label.features.list.fail)
          );
          handleErrorNotify(
            error,
            lang.label.features.list.fail,
            "label.features.error"
          );
          resetData();
          return;
        }
        setStatus((prev) => {
          return {
            ...prev,
            totalValue: res.page.total,
          };
        });

        setData(
          res.result.map((item: FeaturesPopupListData) => {
            return {
              ...item,
              key: item.id,
            };
          })
        );
        setQueryInfo((info) => {
          return {
            ...info,
            ...res.page,
          };
        });
      },
      onError: (e: CustomError) => {
        handleErrorNotify(
          e,
          lang.label.features.list.fail,
          "label.features.error"
        );
        resetData();
      },
    }
  );

  const resetData = () => {
    setData([]);
    setQueryInfo((info) => {
      return {
        ...initialQueryinfo,
      };
    });
  };

  const onHeaderCell = (column: ColumnType<FeaturesPopupListData>) => {
    return {
      onClick: () => {
        if (column?.key) {
          setQueryInfo((info) => {
            return {
              ...info,
              sortType: column?.key as string,
              sortDirection: onSortAntd(info.sortDirection),
              pageNum: 0,
            };
          });
        }
      },
    };
  };

  const onModal = (type: string, row?: FeaturesPopupListData) => {
    if (row) {
      const { createdBy, createdAt, ...rowData } = row;
      setSelectedData({
        ...rowData,
        slides: row.slides.sort((a, b) => a.slideOrder - b.slideOrder),
        isClear: false,
      });
    }

    if (type === "edit" || type === "add") {
      refetch();
      if (type === "add") {
        setSelectedData({
          ...initialData,
        });
      }
      setIsAddFeaturesModal(!isAddFeaturesModal);
    }
    if (type === "preview") {
      setIsViewFeaturesModal(!isViewFeaturesModal);
    }
  };

  const onChangePage = (page: number, totalRows: number) => {
    if (page !== queryInfo.pageNum + 1) {
      queryNavigate({
        to: location.pathname,
        queryInfo: {
          pageLimit: queryInfo.pageLimit,
          pageNum: page - 1,
          keyword: queryInfo.keyword as string,
          sortType: queryInfo.sortType,
          sortDirection: queryInfo.sortDirection as SORT_DIRECTION,
        },
      });
    }
  };

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (queryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: queryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: queryInfo.sortType,
            sortDirection: queryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
    },
    [
      location.pathname,
      queryInfo.keyword,
      queryInfo.pageLimit,
      queryInfo.sortDirection,
      queryInfo.sortType,
      refetch,
      queryNavigate,
    ]
  );

  const titleComponent = "Announcement";
  let buttonLabel = "Add Announcement";

  const columns: TableColumnsType<FeaturesPopupListData> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value, row) => row.title,
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "title"),
      onHeaderCell: onHeaderCell,
    },
    {
      title: "Slides",
      render: (value, row) => formatNumber(row.slides.length),
      width: 100,
      align: "center",
      responsive: ["lg"],
    },
    {
      title: "Display Period",
      render: (value, row) =>
        `${getAccountTimeFormatDate(
          row.startDate,
          {
            ...selectedAccount,
            timezone: "America/Detroit",
          },
          true,
          false
        )} - ${getAccountTimeFormatDate(
          row.endDate,
          {
            ...selectedAccount,
            timezone: "America/Detroit",
          },
          true,
          false
        )}`,
      width: 220,
      responsive: ["md"],
    },
    {
      title: "Date Created",
      key: "createdAt",
      render: (value, row) =>
        getAccountTimeFormatDate(row.createdAt, selectedAccount, true, true),
      width: "15%",
      responsive: ["xl"],
    },
    {
      title: "Display",
      dataIndex: "isActive",
      render: (value, row) => (
        <ToggleComponent
          isToggled={value}
          checked={value}
          disabled={true}
          hideButton={true}
          dataTestId="manager-features-table-button-toggle"
        />
      ),

      width: 100,
      align: "center",
    },
    {
      title: "",
      render: (value, row) => {
        return (
          <div className="table-button-wrapper">
            <TableButton
              onClick={() => {
                onModal("edit", row);
              }}
              label="Edit"
              data-testid="manager-features-table-button-modal"
            />

            <TableButton
              onClick={() => {
                onModal("preview", row);
              }}
              label="Preview"
              data-testid="manager-features-table-button-modal-2"
            />
          </div>
        );
      },
      width: isMobile ? 100 : 180,
    },
  ];

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? info.pageLimit
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "createdAt"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? info.sortDirection
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setQueryInfo((info) => {
        return {
          ...initialQueryinfo,
        };
      });
    }
  }, [location]);

  return (
    <FeaturesPopupPresenter
      titleComponent={titleComponent}
      queryInfo={queryInfo}
      onClickSearch={onClickSearch}
      buttonLabel={buttonLabel}
      columns={columns}
      data={data}
      onChangePage={onChangePage}
      isLoading={isLoading}
      onModal={onModal}
      refetch={refetch}
      isAddFeaturesModal={isAddFeaturesModal}
      selectedData={selectedData}
      setSelectedData={setSelectedData}
      isViewFeaturesModal={isViewFeaturesModal}
      status={status}
    />
  );
}
